import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  GraduationCap, 
  Award, 
  DollarSign,
  CreditCard,
  Building,
  Plus,
  X,
  Save,
  Edit,
  Upload,
  Eye,
  EyeOff,
  IndianRupee
} from 'lucide-react';
import { updateDoctor } from '../../../services/doctorApi';

const DoctorDetailsForm = ({ doctorId, initialDoctorData, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    gender: '',
    avatar: '',
    contact: {
      mobile: '',
      email: '',
      address: ''
    },
    specialization: [],
    experience: '',
    education: [],
    certifications: [],
    consultationFee: '',
    bankDetails: {
      accountNumber: '',
      ifscCode: '',
      bankName: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Available specializations
  const specializations = [
    'General Medicine',
    'Pediatrics',
    'Cardiology',
    'Dermatology',
    'Orthopedics',
    'Neurology',
    'Gynecology',
    'Psychiatry',
    'Radiology',
    'Anesthesiology',
    'Emergency Medicine',
    'Internal Medicine',
    'Surgery',
    'Oncology',
    'Ophthalmology',
    'ENT',
    'Urology',
    'Endocrinology',
    'Gastroenterology',
    'Pulmonology'
  ];

  const genderOptions = ['Male', 'Female'];

  // Load doctor data on component mount
  useEffect(() => {
    if (initialDoctorData) {
      loadDoctorData(initialDoctorData);
    }
    console.log("Initial Doctor Data:", initialDoctorData);
    
  }, [initialDoctorData]);

  const loadDoctorData = (doctorData) => {
    setFormData({
      name: doctorData.name || '',
      dateOfBirth: doctorData.dateOfBirth ? new Date(doctorData.dateOfBirth).toISOString().split('T')[0] : '',
      gender: doctorData.gender || '',
      avatar: doctorData.avatar || '',
      contact: {
        mobile: doctorData.contact?.mobile || '',
        email: doctorData.contact?.email || '',
        address: doctorData.contact?.address || ''
      },
      specialization: doctorData.specialization || [],
      experience: doctorData.experience || '',
      education: doctorData.education || [],
      certifications: doctorData.certifications || [],
      consultationFee: doctorData.consultationFee || '',
      bankDetails: {
        accountNumber: doctorData.bankDetails?.accountNumber || '',
        ifscCode: doctorData.bankDetails?.ifscCode || '',
        bankName: doctorData.bankDetails?.bankName || ''
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSpecializationChange = (specialization) => {
    setFormData(prev => ({
      ...prev,
      specialization: prev.specialization.includes(specialization)
        ? prev.specialization.filter(s => s !== specialization)
        : [...prev.specialization, specialization]
    }));
  };

  // Avatar upload function
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, or WebP).");
      return;
    }

    // Validate file size (e.g., max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError("File size must be less than 5MB.");
      return;
    }

    setIsUploadingAvatar(true);

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("upload_preset", "mypreset");
    uploadFormData.append("cloud_name", "dsi1mb2aj");

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
        {
          method: "POST",
          body: uploadFormData,
        }
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      const avatarUrl = data.secure_url;

      // Update the form data with the new avatar URL
      setFormData((prevData) => ({
        ...prevData,
        avatar: avatarUrl,
      }));

      setSuccess("Avatar uploaded successfully!");
      setError('');
    } catch (error) {
      console.error("Error uploading avatar:", error);
      setError("Failed to upload avatar. Please try again.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [
        ...prev.education,
        { degree: '', institution: '', year: '' }
      ]
    }));
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const updateEducation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        { 
          name: '', 
          issuedBy: '', 
          issuedDate: '', 
          expiryDate: '', 
          certificateUrl: '' 
        }
      ]
    }));
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const updateCertification = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Validate required fields
      if (!formData.name || !formData.contact.email || !formData.contact.mobile) {
        setError('Please fill in all required fields');
        return;
      }

      const response = await updateDoctor(doctorId, formData);
      
      if (response.success) {
        setSuccess('Doctor details updated successfully!');
        setEditMode(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      setError(error.message || 'Failed to update doctor details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCancel = () => {
    setEditMode(false);
    setError('');
    setSuccess('');
    // Reset form data to original values
    if (initialDoctorData) {
      loadDoctorData(initialDoctorData);
    }
    if (onCancel) onCancel();
  };

  if (!initialDoctorData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto py-6 bg-white rounded-lg shadow-lg">
      {/* Header with Profile Picture */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Doctor Profile</h2>
          <div className="flex space-x-3">
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Saving...' : 'Save'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Picture Section */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-20 h-20 bg-gray-100 rounded-full overflow-hidden">
              <img
                src={
                  formData.avatar || `https://i.pravatar.cc/150?u=${doctorId}`
                }
                alt="Doctor Profile"
                className="w-full h-full object-cover"
              />
              {isUploadingAvatar && (
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {editMode && (
              <div className="absolute -bottom-1 -right-1">
                <label
                  htmlFor="avatar-upload"
                  className="bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <Upload className="w-3 h-3" />
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {formData.name || 'Dr. [Name]'}
            </h3>
            <p className="text-sm text-gray-500">
              {formData.specialization.length > 0 
                ? formData.specialization.join(', ')
                : 'Medical Professional'
              }
            </p>
            {editMode && (
              <p className="text-xs text-gray-400 mt-1">
                JPEG, PNG, GIF, or WebP. Max 5MB.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="px-6">
        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 mt-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 mt-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8 mt-6">
          {/* Personal Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                {editMode ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.name || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                {editMode ? (
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formatDate(formData.dateOfBirth)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                {editMode ? (
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Gender</option>
                    {genderOptions.map(gender => (
                      <option key={gender} value={gender}>{gender}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900 py-2">{formData.gender || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience (Years)
                </label>
                {editMode ? (
                  <input
                    type="number"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.experience ? `${formData.experience} years` : 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                {editMode ? (
                  <input
                    type="tel"
                    name="contact.mobile"
                    value={formData.contact.mobile}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.contact.mobile || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                {editMode ? (
                  <input
                    disabled
                    type="email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
                    required
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.contact.email || 'Not specified'}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                {editMode ? (
                  <textarea
                    name="contact.address"
                    value={formData.contact.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{formData.contact.address || 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Specialization */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="w-5 h-5 mr-2" />
              Specialization
            </h3>
            
            {editMode ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {specializations.map(specialization => (
                  <label key={specialization} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.specialization.includes(specialization)}
                      onChange={() => handleSpecializationChange(specialization)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{specialization}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {formData.specialization.length > 0 ? (
                  formData.specialization.map(spec => (
                    <span key={spec} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {spec}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500">No specializations specified</p>
                )}
              </div>
            )}
          </div>

          {/* Education */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2" />
                Education
              </h3>
              {editMode && (
                <button
                  type="button"
                  onClick={addEducation}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg flex items-center space-x-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Education</span>
                </button>
              )}
            </div>
            
            {formData.education.length > 0 ? (
              <div className="space-y-4">
                {formData.education.map((edu, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                    {editMode ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Degree
                          </label>
                          <input
                            type="text"
                            value={edu.degree}
                            onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Institution
                          </label>
                          <input
                            type="text"
                            value={edu.institution}
                            onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Year
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="number"
                              value={edu.year}
                              onChange={(e) => updateEducation(index, 'year', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              min="1950"
                              max="2030"
                            />
                            <button
                              type="button"
                              onClick={() => removeEducation(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium text-gray-900">{edu.degree || 'Not specified'}</p>
                        <p className="text-gray-600">{edu.institution || 'Not specified'}</p>
                        <p className="text-sm text-gray-500">{edu.year || 'Year not specified'}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No education records added</p>
            )}
          </div>

          {/* Certifications */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="w-5 h-5 mr-2" />
                Certifications
              </h3>
              {editMode && (
                <button
                  type="button"
                  onClick={addCertification}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg flex items-center space-x-1 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Certification</span>
                </button>
              )}
            </div>
            
            {formData.certifications.length > 0 ? (
              <div className="space-y-4">
                {formData.certifications.map((cert, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                    {editMode ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Certification Name
                          </label>
                          <input
                            type="text"
                            value={cert.name}
                            onChange={(e) => updateCertification(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Issued By
                          </label>
                          <input
                            type="text"
                            value={cert.issuedBy}
                            onChange={(e) => updateCertification(index, 'issuedBy', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Issued Date
                          </label>
                          <input
                            type="date"
                            value={cert.issuedDate ? new Date(cert.issuedDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => updateCertification(index, 'issuedDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiry Date
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="date"
                              value={cert.expiryDate ? new Date(cert.expiryDate).toISOString().split('T')[0] : ''}
                              onChange={(e) => updateCertification(index, 'expiryDate', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                              type="button"
                              onClick={() => removeCertification(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Certificate URL
                          </label>
                          <input
                            type="url"
                            value={cert.certificateUrl}
                            onChange={(e) => updateCertification(index, 'certificateUrl', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/certificate"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium text-gray-900">{cert.name || 'Not specified'}</p>
                        <p className="text-gray-600">{cert.issuedBy || 'Issuer not specified'}</p>
                        <div className="text-sm text-gray-500 mt-1">
                          <p>Issued: {cert.issuedDate ? formatDate(cert.issuedDate) : 'Not specified'}</p>
                          <p>Expires: {cert.expiryDate ? formatDate(cert.expiryDate) : 'Not specified'}</p>
                        </div>
                        {cert.certificateUrl && (
                          <a
                            href={cert.certificateUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm mt-2 inline-block"
                          >
                            View Certificate
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No certifications added</p>
            )}
          </div>

          {/* Professional Details */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <IndianRupee className="w-5 h-5 mr-2" />
              Professional Details
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consultation Fee
              </label>
              {editMode ? (
                <input
                  type="number"
                  name="consultationFee"
                  value={formData.consultationFee}
                  onChange={handleInputChange}
                  className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              ) : (
                <p className="text-gray-900 py-2">
                  {formData.consultationFee ? `₹${formData.consultationFee}` : 'Not specified'}
                </p>
              )}
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Bank Details
              </h3>
              <button
                type="button"
                onClick={() => setShowBankDetails(!showBankDetails)}
                className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
              >
                {showBankDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>{showBankDetails ? 'Hide' : 'Show'} Bank Details</span>
              </button>
            </div>
            
            {showBankDetails && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="bankDetails.accountNumber"
                      value={formData.bankDetails.accountNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">
                      {formData.bankDetails.accountNumber ? '****' + formData.bankDetails.accountNumber.slice(-4) : 'Not specified'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IFSC Code
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="bankDetails.ifscCode"
                      value={formData.bankDetails.ifscCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{formData.bankDetails.ifscCode || 'Not specified'}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="bankDetails.bankName"
                      value={formData.bankDetails.bankName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="text-gray-900 py-2">{formData.bankDetails.bankName || 'Not specified'}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons - Only show when in edit mode */}
          {editMode && (
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || isUploadingAvatar}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default DoctorDetailsForm;